import React from 'react';

import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { listingItemPagePath } from '@reverbdotcom/commons/src/url_helpers';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import { WEB } from '@reverbdotcom/commons/src/constants';
import { RCCheckbox, RCImage, useMediaQuery } from '@reverbdotcom/cadence/components';

import QuickPriceEditButton, {
  VALID_LISTING_STATES_FOR_QUICK_PRICE_EDIT,
} from '../listings/quick_price_edit/quick_price_edit_button';
import SellerListingCardDetails from './SellerListingCardDetails';
import SellerListingCardFooter from './SellerListingCardFooter';
import BLANK_MEDIUM_IMAGE from '../../../images/products/blank_medium.jpg';
import PublishErrors from './PublishErrors';
import LoadingOverlay from '../../shared/loading_overlay';
import SellerListingsCollectionContext from './SellerListingsCollectionContext';
import { SellerListing, ActionType } from './sellerListingsCollectionReducer';
import SellerListingCardInsightsActionMenu from './SellerListingCardInsightsActionMenu';
import ListingState from './ListingState';
import TopPickWinnerPresentment from './TopPickWinnerPresentment';
import TopPickEligiblePresentment from './TopPickEligiblePresentment';
import LowVisibilityPresentment from './LowVisibilityPresentment';
import RenewListingPresentment from './RenewListingPresentment';
import PriceDropRecommendationPresentment from './PriceDropRecommendationPresentment';
import { hasInsightRecommendation, hasPriceDropRecommendation } from './insightHelpers';
import NBAPriceDropPresentment from './NBAPriceDropPresentment';
import NBADirectOffersPresentment from './NBADirectOffersPresentment';
import NBAPromoteWithBumpPresentment from './NBAPromoteWithBumpPresentment';
import PublishListingPresentment from './PublishListingPresentment';
import { DRAFT } from './toolbarHelpers';

export const COMPONENT_NAME = 'SellerListingCard';

function InsightPresentment({ listing }: { listing: SellerListing }) {
  if (listing.state === DRAFT) {
    return <PublishListingPresentment listing={listing} />;
  }

  const nextBestAction = listing.insights?.nextBestAction;

  // Prefer NBA recommendations while we transition to them fully
  if (nextBestAction?.actionType === 'RENEW') {
    return <RenewListingPresentment listing={listing} nextBestAction={nextBestAction} />;
  }

  if (nextBestAction?.actionType === 'DIRECT_OFFER') {
    return <NBADirectOffersPresentment listing={listing} nextBestAction={nextBestAction} />;
  }

  if (nextBestAction?.actionType === 'PRICE_DROP') {
    return <NBAPriceDropPresentment listing={listing} nextBestAction={nextBestAction} />;
  }

  if (nextBestAction?.actionType === 'PROMOTE') {
    return <NBAPromoteWithBumpPresentment listing={listing} nextBestAction={nextBestAction} />;
  }

  const insight = listing.featuredListingInsight;
  const renewEligible = Boolean(listing?.renewalEligibility?.isEligible);
  if (renewEligible) {
    return <RenewListingPresentment listing={listing} />;
  }

  if (insight?.featuredWinner) {
    return <TopPickWinnerPresentment listing={listing} />;
  }

  if (insight?.eligibleForTopSpot) {
    return <TopPickEligiblePresentment listing={listing} />;
  }

  if (listing.lowVisibility) {
    return <LowVisibilityPresentment listing={listing} />;
  }

  if (hasPriceDropRecommendation(listing)) {
    return <PriceDropRecommendationPresentment listing={listing} />;
  }

  return null;
}

export default function SellerListingCard({
  listing,
  showQuickPriceEditPopover = false,
}: {
  listing: SellerListing;
  showQuickPriceEditPopover?: boolean;
}) {
  const user = useUser();
  const openNewTab = user && user.deviceName === WEB;
  const isMobile = useMediaQuery('mobile');

  const {
    listingsCollectionState,
    dispatch: selectionDispatch,
  } = React.useContext(SellerListingsCollectionContext);

  const isSelected = !!listingsCollectionState.selectedListings.find(selectedListing => selectedListing.id === listing.id);

  function handleSelection() {
    if (isSelected) {
      selectionDispatch({
        type: ActionType.DESELECT_LISTING,
        payload: { listing },
      });
    } else {
      selectionDispatch({
        type: ActionType.SELECT_LISTING,
        payload: { listing },
      });
    }
  }

  const isEditable = VALID_LISTING_STATES_FOR_QUICK_PRICE_EDIT.includes(listing.state);
  const isRelistEnabled = Boolean(listing?.renewalEligibility?.isEligible);

  return (
    <div className="seller-listing-card seller-listing-card--insights">
      <LoadingOverlay showOverlay={listing.loading}>
        <div className="seller-listing-card-insights__layout">
          <div className="seller-listing-card-insights__main">
            <div className="seller-listing-card__content">
              <div className="d-flex fx-justify-between width-100">
                <div className="d-flex fx-grow min-width-0 gap-4">
                  <RCCheckbox
                    checked={isSelected}
                    onChange={handleSelection}
                    label={listing.title}
                    visuallyHiddenLabel
                  />

                  <div className="seller-listing-card__content__image">
                    <CoreLink
                      to={listingItemPagePath(listing)}
                      target={openNewTab ? '_blank' : undefined}
                    >
                      <RCImage
                        src={listing.images[0]?.source || BLANK_MEDIUM_IMAGE}
                        imageBox="fullWidth"
                        alt={listing.title}
                      />
                    </CoreLink>
                  </div>
                  <div className="d-flex fx-dir-col gap-2 fx-grow min-width-0">
                    <ListingState listing={listing} isRelistEnabled={isRelistEnabled} />
                    <CoreLink
                      className="seller-listing-card__content__listing-title"
                      to={listingItemPagePath(listing)}
                      target={openNewTab ? '_blank' : undefined}
                    >
                      <span className="seller-listing-card-insights__title">{listing.title}</span>
                    </CoreLink>

                    <div>
                      <SellerListingCardDetails listing={listing} />
                      <PublishErrors listing={listing} />
                    </div>

                    {isMobile && (
                      <div className="d-flex fx-dir-col fx-align-start gap-2">
                        <QuickPriceEditButton
                          listing={listing}
                          showQuickPriceEditPopover={showQuickPriceEditPopover}
                          isEditable={isEditable}
                          hidePriceRecommendationCallout={true}
                        />
                      </div>
                    )}
                  </div>
                </div>
                {!isMobile && (
                  <div className="d-flex fx-dir-col fx-align-end">
                    <div className="d-flex fx-dir-row gap-2">
                      <QuickPriceEditButton
                        listing={listing}
                        showQuickPriceEditPopover={showQuickPriceEditPopover}
                        isEditable={isEditable}
                        hidePriceRecommendationCallout={true}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            <SellerListingCardFooter listing={listing} isMobile={isMobile} />
          </div>

          {(!isMobile || hasInsightRecommendation(listing)) && (
            <div className="seller-listing-card-insights__actions-panel">
              <InsightPresentment listing={listing} />
              {!isMobile && <SellerListingCardInsightsActionMenu listing={listing} hasRecommendation={hasInsightRecommendation(listing)} />}
            </div>
          )}
        </div>

      </LoadingOverlay>
    </div>
  );
}
